import React from 'react';
import {
  Button,
  Flex,
  Text,
  useColorMode,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  Icon,
  Grid,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { CaretDown, PlusCircle, XCircle } from '@phosphor-icons/react';
import { Formik } from 'formik';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment';

interface FilterDateRangePillProps {
  clear: () => void;
  setValues: (values: { startDate: string; endDate: string }) => void;
  initialValues: { startDate: string; endDate: string };
  label: string;
  isDisabled?: boolean;
}

export const FilterDateRangePill: React.FC<FilterDateRangePillProps> = ({ isDisabled, clear, initialValues, label, setValues }) => {
  const { colorMode } = useColorMode();

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return moment(dateString).format('DD.MM.YYYY');
  };

  const getSelectedText = () => {
    if (!initialValues.startDate && !initialValues.endDate) return false;

    if (initialValues.startDate && initialValues.endDate) {
      return `${formatDate(initialValues.startDate)} - ${formatDate(initialValues.endDate)}`;
    } else if (initialValues.startDate) {
      return `Ab ${formatDate(initialValues.startDate)}`;
    } else if (initialValues.endDate) {
      return `Bis ${formatDate(initialValues.endDate)}`;
    }
    return false;
  };

  const selectedText = getSelectedText();

  return (
    <Popover placement="bottom-start">
      {/* @ts-ignore */}
      <PopoverTrigger>
        <Flex
          border={selectedText ? '1px solid' : '1px dashed'}
          borderColor="gray.300"
          color={colorMode === 'light' ? 'gray.500' : 'gray.400'}
          alignItems="center"
          gap="1"
          rounded="full"
          fontSize="xs"
          fontWeight="semibold"
          px="1.5"
          py="0.5"
          cursor="pointer"
          transition="all"
          pointerEvents={isDisabled ? 'none' : 'all'}
          opacity={isDisabled ? '0.2' : '1'}
          _hover={{
            bg: colorMode === 'light' ? 'gray.100' : 'gray.700',
            color: colorMode === 'light' ? 'gray.600' : 'gray.200',
          }}
        >
          {selectedText ? (
            <Icon
              as={XCircle}
              h="3"
              w="3"
              onClick={(event) => {
                event.stopPropagation();
                clear();
              }}
            />
          ) : (
            <Icon
              as={PlusCircle}
              h="3"
              w="3"
              _hover={{
                color: 'gray.900',
              }}
            />
          )}
          <Text as="span">{label}</Text>
          {selectedText ? (
            <>
              <Text as="span" fontSize="10px">{` | `}</Text>
              <Text as="span" color="blue.500">
                {selectedText}
              </Text>
              <Icon
                as={CaretDown}
                h="4"
                w="4"
                _hover={{
                  color: 'gray.900',
                }}
              />
            </>
          ) : (
            ''
          )}
        </Flex>
      </PopoverTrigger>

      <PopoverContent className="bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-800 rounded p-3" w="300px">
        <>
          <PopoverArrow />
          <Formik
            initialValues={{
              startDate: initialValues.startDate || '',
              endDate: initialValues.endDate || '',
            }}
            enableReinitialize
            onSubmit={(values) => {
              setValues(values);
            }}
          >
            {({ values, setFieldValue, handleSubmit }) => {
              return (
                <Grid p="2" gap="3">
                  <FormControl>
                    <FormLabel fontSize="sm" mb="1">
                      Von
                    </FormLabel>
                    <ReactDatePicker
                      selected={values.startDate ? moment(values.startDate).toDate() : null}
                      onChange={(date) => {
                        setFieldValue('startDate', date ? moment(date).toISOString() : '');
                      }}
                      selectsStart
                      startDate={values.startDate ? moment(values.startDate).toDate() : undefined}
                      endDate={values.endDate ? moment(values.endDate).toDate() : undefined}
                      placeholderText="TT.MM.JJJJ"
                      dateFormat="dd.MM.yyyy"
                      locale="de"
                      isClearable
                      showPopperArrow={false}
                      popperPlacement="bottom-start"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" mb="1">
                      Bis
                    </FormLabel>
                    <ReactDatePicker
                      selected={values.endDate ? moment(values.endDate).toDate() : null}
                      onChange={(date) => {
                        setFieldValue('endDate', date ? moment(date).toISOString() : '');
                      }}
                      selectsEnd
                      startDate={values.startDate ? moment(values.startDate).toDate() : undefined}
                      endDate={values.endDate ? moment(values.endDate).toDate() : undefined}
                      minDate={values.startDate ? moment(values.startDate).toDate() : undefined}
                      placeholderText="TT.MM.JJJJ"
                      dateFormat="dd.MM.yyyy"
                      locale="de"
                      isClearable
                      showPopperArrow={false}
                      popperPlacement="bottom-start"
                    />
                  </FormControl>

                  <Button colorScheme="teal" size="sm" fontWeight="medium" w="full" mt="2" onClick={() => handleSubmit()}>
                    Speichern
                  </Button>
                </Grid>
              );
            }}
          </Formik>
        </>
      </PopoverContent>
    </Popover>
  );
};
